import { useState, useMemo } from "react";
import { Phone, Mail, MapPin, MessageCircle, Clock, Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { waLink, telLink, track, newIdempotencyKey, LOAN_TYPES } from "../lib/site";
import ContactForm from "../components/site/ContactForm";

export default function Contact() {
  const { settings } = useSite();
  const idemKey = useMemo(newIdempotencyKey, []);
  const [cb, setCb] = useState({ name: "", phone: "", loan_type: "", preferred_date: "", preferred_time: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setCb((f) => ({ ...f, [k]: e.target.value }));

  const submitCallback = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const p = new URLSearchParams(window.location.search);
      const { data } = await api.post("/callbacks", {
        name: cb.name, phone: cb.phone, loan_type: cb.loan_type, message: cb.message,
        preferred_time: `${cb.preferred_date} — ${cb.preferred_time}`,
        idempotency_key: idemKey,
        utm_source: p.get("utm_source") || "", utm_medium: p.get("utm_medium") || "", utm_campaign: p.get("utm_campaign") || "",
      });
      track("callback_request", { lead_id: data.lead_id });
      setDone(data);
      toast.success("Callback requested. Our team will reach out at your preferred time.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const cards = [
    { icon: Phone, label: "Call Us", value: settings.phone_display || settings.phone, cta: "Call Now", href: telLink(settings), testid: "contact-phone", event: "phone_click" },
    { icon: Mail, label: "Email Us", value: settings.email, cta: "Send Email", href: `mailto:${settings.email}`, testid: "contact-email" },
    { icon: MessageCircle, label: "WhatsApp", value: "Chat with our team", cta: "WhatsApp Us", href: waLink(settings), testid: "contact-whatsapp", event: "whatsapp_click", external: true },
    { icon: MapPin, label: "Visit Us", value: settings.address, cta: null, href: null, testid: "contact-address" },
  ];

  return (
    <div data-testid="contact-page">
      <section className="container-site pt-14 lg:pt-20 pb-10">
        <p className="eyebrow">Contact BharatBest Fintech</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold leading-[1.08] text-brand-dark" data-testid="contact-title">We're Here to Help</h1>
        <p className="mt-5 text-base md:text-lg text-brand-muted leading-relaxed max-w-2xl">
          Have a question about a loan or financial solution? Get in touch with the BharatBest Fintech team. {settings.hours}
        </p>
      </section>

      <section className="container-site grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {cards.map((c, i) => (
          <div key={i} data-testid={c.testid} className="card-site p-6 flex flex-col">
            <c.icon size={22} className="text-brand-red" />
            <p className="mt-4 text-xs uppercase tracking-[0.15em] font-bold text-brand-muted">{c.label}</p>
            <p className="mt-1.5 font-heading font-bold text-sm text-brand-dark break-words flex-1">{c.value}</p>
            {c.cta && (
              <a href={c.href} target={c.external ? "_blank" : undefined} rel="noreferrer"
                onClick={() => c.event && track(c.event)}
                data-testid={`${c.testid}-cta`}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-brand-red hover:opacity-80 transition-opacity duration-200">
                {c.cta} <ArrowRight size={14} />
              </a>
            )}
          </div>
        ))}
      </section>

      <section className="container-site py-14 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          <div className="flex flex-col gap-6">
            <div className="card-site p-6 sm:p-8 flex-1" data-testid="contact-info-card">
              <h2 className="font-heading text-xl font-bold text-brand-dark mb-5">Contact Information</h2>
              <div className="space-y-5 text-sm">
                <a href={telLink(settings)} onClick={() => track("phone_click")} className="flex items-center gap-3 group">
                  <span className="w-10 h-10 rounded-xl bg-brand-red/5 text-brand-red flex items-center justify-center shrink-0"><Phone size={17} /></span>
                  <div><p className="text-xs text-brand-muted font-semibold">Phone</p><p className="font-bold text-brand-dark group-hover:text-brand-red transition-colors duration-200">{settings.phone_display || settings.phone}</p></div>
                </a>
                <a href={`mailto:${settings.email}`} className="flex items-center gap-3 group">
                  <span className="w-10 h-10 rounded-xl bg-brand-red/5 text-brand-red flex items-center justify-center shrink-0"><Mail size={17} /></span>
                  <div><p className="text-xs text-brand-muted font-semibold">Email</p><p className="font-bold text-brand-dark group-hover:text-brand-red transition-colors duration-200 break-all">{settings.email}</p></div>
                </a>
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-xl bg-brand-red/5 text-brand-red flex items-center justify-center shrink-0"><MapPin size={17} /></span>
                  <div><p className="text-xs text-brand-muted font-semibold">Office Address</p><p className="font-semibold text-brand-dark leading-relaxed">{settings.address}</p></div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-brand-red/5 text-brand-red flex items-center justify-center shrink-0"><Clock size={17} /></span>
                  <div><p className="text-xs text-brand-muted font-semibold">Working Hours</p><p className="font-semibold text-brand-dark">{settings.hours}</p></div>
                </div>
              </div>
            </div>
            {settings.maps_embed_url && (
              <div className="card-site overflow-hidden" data-testid="contact-map">
                <iframe title="BharatBest Fintech office location" src={settings.maps_embed_url}
                  className="w-full h-64 border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
            )}
          </div>
          <ContactForm />
        </div>
      </section>

      <section className="container-site pb-16 lg:pb-24">
        <div className="max-w-2xl mx-auto card-site p-6 sm:p-8" data-testid="callback-form-card">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-10 h-10 rounded-xl bg-brand-red/5 text-brand-red flex items-center justify-center"><Clock size={18} /></span>
            <h2 className="font-heading text-xl font-bold text-brand-dark">Request a Callback</h2>
          </div>
          <p className="text-sm text-brand-muted mb-6">Tell us when to call — a BharatBest advisor will reach out at your preferred time.</p>
          {done ? (
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-6 text-sm text-emerald-800" data-testid="callback-success">
              <CheckCircle2 size={20} className="mb-2" />
              Thank you, {cb.name}. Your callback request has been recorded (Lead ID: <strong>{done.lead_id}</strong>). Our team will contact you {cb.preferred_time ? `around ${cb.preferred_time}` : "shortly"}.
            </div>
          ) : (
            <form onSubmit={submitCallback} className="grid sm:grid-cols-2 gap-4" data-testid="callback-form">
              <input required data-testid="callback-name" className="input-site" placeholder="Full Name *" value={cb.name} onChange={set("name")} aria-label="Full name" />
              <input required data-testid="callback-phone" className="input-site" placeholder="Contact Number *" value={cb.phone} onChange={set("phone")} inputMode="tel" aria-label="Contact number" />
              <select data-testid="callback-loan-type" className="input-site" value={cb.loan_type} onChange={set("loan_type")} aria-label="Loan type">
                <option value="">Loan Type</option>
                {LOAN_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input required type="date" data-testid="callback-date" className="input-site" value={cb.preferred_date} onChange={set("preferred_date")} aria-label="Preferred date" />
                <select data-testid="callback-time" className="input-site" value={cb.preferred_time} onChange={set("preferred_time")} aria-label="Preferred time">
                  <option value="">Time</option>
                  <option>Morning (10 AM – 12 PM)</option>
                  <option>Afternoon (12 – 3 PM)</option>
                  <option>Evening (3 – 7 PM)</option>
                </select>
              </div>
              <textarea data-testid="callback-message" className="input-site sm:col-span-2 min-h-[80px]" placeholder="Message (optional)" value={cb.message} onChange={set("message")} aria-label="Message" />
              <button type="submit" disabled={loading} data-testid="callback-submit-btn" className="btn-primary sm:col-span-2">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <>Request Callback <ArrowRight size={15} /></>}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
